import {
	Box,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	type TableSortLabelOwnProps,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, type ReactNode, type SyntheticEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChangeSourceBadge } from "../../components/UIToolkit/Badges/ChangeSourceBadge";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "../../components/UIToolkit/Inputs/SystemDropDown";
import type { SystemTextFieldProps } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { DateRangeSelector } from "../../components/UIToolkit/molecules/DateRangeSelector";
import type { UserInfo } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { INK, NEUTRAL, STATUS_BADGE, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { ADMIN_ROLES, type AdminRole, type Nurse } from "../../utils/models";
import { type ActivityLogEntry, useActivityLogQuery } from "./useActivityLogQuery";

type SortField = keyof ActivityLogEntry;
type SortOrder = Exclude<TableSortLabelOwnProps["direction"], undefined>;

const minWidth = { xs: 210, xxl: 300 } as const satisfies { xs: number | string; xxl: number | string };

const commonTextFieldProps: SystemTextFieldProps = {
	inputSx: { minWidth: minWidth },
	reserveHeightForHelperText: false,
};

const TableHeader: FC<{
	field: SortField;
	label: string;
	sortField: string;
	sortOrder: TableSortLabelOwnProps["direction"];
	handleSort: (_: SortField) => void;
}> = ({ field, label, sortField, sortOrder, handleSort }) => (
	<TableCell>
		<TableSortLabel
			active={sortField === field}
			direction={sortField === field ? sortOrder : "asc"}
			onClick={() => handleSort(field)}
			sx={{
				"& .MuiTableSortLabel-icon": {
					color: `${INK.SECONDARY} !important`,
				},
				color: INK.SECONDARY,
				fontWeight: "bold",
			}}
		>
			{label}
		</TableSortLabel>
	</TableCell>
);

type Props = {
	titleTrailingNode?: ReactNode;
};

export const ActivityLog: FC<Props> = ({ titleTrailingNode }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const { roster, getNurseFormattedName, isLoading: isLoadingRoster, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);
	const [start, setStart] = useState<DateTime>(DateTime.now().minus({ day: 7 }).startOf("day"));
	const [end, setEnd] = useState<DateTime>(DateTime.now().startOf("day"));
	const [pageFilter, setPageFilter] = useState("");
	const [tableNameFilter, setTableNameFilter] = useState("");
	const [performedForFilter, setPerformedForFilter] = useState<Nurse["id"]>();
	const [performedByFilter, setPerformedByFilter] = useState<UserInfo["username"]>();
	const [roleFilter, setRoleFilter] = useState<AdminRole>();
	const [reasonFilter, setReasonFilter] = useState<string>();

	const [detailsFromDateValue, setDetailsFromDateValue] = useState<DateTime | null>(null);
	const [detailsToDateValue, setDetailsToDateValue] = useState<DateTime | null>(null);

	const [sortField, setSortField] = useState<SortField>("timestamp");
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

	const detailsFromDate = useDebouncedValue(detailsFromDateValue);
	const detailsToDate = useDebouncedValue(detailsToDateValue);

	const { data: activityLogs = [], isLoading: isLoadingLogs } = useActivityLogQuery({
		start,
		end,
		changerUsername: performedByFilter,
		changeeNurseId: performedForFilter,
	});
	// "Performed For" names come from the roster, so the table waits for it too.
	const isLoading = isLoadingLogs || isLoadingRoster;

	const clearDetailsDate = () => {
		setDetailsFromDateValue(null);
		setDetailsToDateValue(null);
	};

	const handleSort = (field: SortField) => {
		const newSortOrder = field === sortField && sortOrder === "asc" ? "desc" : "asc";
		setSortField(field);
		setSortOrder(newSortOrder);

		trackEvent("activity_log_sort_changed", {
			department_id: chosenDepartment?.department_id,
			sort_field: field,
			sort_order: newSortOrder,
			entry_count: activityLogs.length,
		});
	};

	const handleFromDateChange = (date: DateTime) => {
		setStart(date);

		trackEvent("activity_log_date_filter_changed", {
			department_id: chosenDepartment?.department_id,
			filter_type: "from_date",
			date_value: date.toFormat("yyyy-MM-dd"),
			has_to_date: !!end,
		});
	};

	const handleToDateChange = (date: DateTime) => {
		setEnd(date);

		trackEvent("activity_log_date_filter_changed", {
			department_id: chosenDepartment?.department_id,
			filter_type: "to_date",
			date_value: date.toFormat("yyyy-MM-dd"),
			has_from_date: !!start,
		});
	};

	const handleDetailsFromDateChange = (date: DateTime) => {
		setDetailsFromDateValue(date);

		trackEvent("activity_log_details_date_filter_changed", {
			department_id: chosenDepartment?.department_id,
			filter_type: "details_from_date",
			date_value: date.toFormat("yyyy-MM-dd"),
			has_details_to_date: !!detailsToDateValue,
		});
	};

	const handleDetailsToDateChange = (date: DateTime) => {
		setDetailsToDateValue(date);

		trackEvent("activity_log_details_date_filter_changed", {
			department_id: chosenDepartment?.department_id,
			filter_type: "details_to_date",
			date_value: date.toFormat("yyyy-MM-dd"),
			has_details_from_date: !!detailsFromDateValue,
		});
	};

	const handlePageFilterChange = (newValue?: string) => {
		setPageFilter(newValue || "");

		trackEvent("activity_log_page_filter_changed", {
			department_id: chosenDepartment?.department_id,
			page_filter: newValue || "all",
		});
	};

	const handleRoleFilterChange = (newValue?: AdminRole) => {
		setRoleFilter(newValue);
		track("activity_log_role_filter_changed", { role_filter: newValue || "all" });
	};

	const handleTableNameFilterChange = (newValue?: string) => {
		setTableNameFilter(newValue || "");

		trackEvent("activity_log_table_name_filter_changed", {
			department_id: chosenDepartment?.department_id,
			table_name_filter: newValue || "all",
		});
	};

	const handlePerformedForFilterChange = (_event: SyntheticEvent, newValue: SystemAutoCompleteOption<number> | null) => {
		setPerformedForFilter(newValue?.value);

		trackEvent("activity_log_performer_filter_changed", {
			department_id: chosenDepartment?.department_id,
			performer_filter: newValue?.value || "all",
		});
	};

	const handlePerformedByFilterChange = (_event: SyntheticEvent, newValue: SystemAutoCompleteOption<string> | null) => {
		setPerformedByFilter(newValue?.value);

		trackEvent("activity_log_performed_by_filter_changed", {
			department_id: chosenDepartment?.department_id,
			performed_by_filter: newValue?.value || "all",
		});
	};

	const handleReasonFilterChange = (_event: SyntheticEvent, newValue: SystemAutoCompleteOption<string> | null) => {
		setReasonFilter(newValue?.value);

		trackEvent("activity_log_reason_filter_changed", {
			department_id: chosenDepartment?.department_id,
			reason_filter: newValue?.value || "all",
		});
	};

	const sortedActivityLogs = useMemo(() => {
		return activityLogs.slice().sort((a, b) => {
			const compareValue = sortOrder === "asc" ? 1 : -1;

			if (sortField === "timestamp") {
				const dateA = DateTime.fromISO(a[sortField]);
				const dateB = DateTime.fromISO(b[sortField]);
				return dateA.diff(dateB, "minute").minutes * compareValue;
			}

			return ((a[sortField] ?? "") > (b[sortField] ?? "") ? 1 : -1) * compareValue;
		});
	}, [activityLogs, sortField, sortOrder]);

	const reasonOptions = useMemo(() => {
		const counts = new Map<string, number>();

		for (const log of activityLogs) {
			const candidates = [log.changes?.reason, log.changes?.message];
			for (const v of candidates) {
				if (typeof v === "string" && v.trim()) {
					counts.set(v, (counts.get(v) ?? 0) + 1);
				}
			}
		}

		return Array.from(counts.entries())
			.filter(([, count]) => count >= 5)
			.map(([value]) => value)
			.sort()
			.map((value) => ({ label: value, value }));
	}, [activityLogs]);

	const formatTimestamp = (date: DateTime) => {
		const dateObj = date.toJSDate();

		const datePart = dateObj.toLocaleDateString("en-US", {
			weekday: "short",
			month: "short",
			day: "numeric",
			year: "numeric",
		});

		const timePart = dateObj.toLocaleTimeString("en-US", {
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: false,
		});

		return (
			<Box component="span">
				<Typography component="span" sx={{ opacity: 1, ml: 1 }}>
					{datePart}
				</Typography>
				<Typography component="span" sx={{ opacity: 0.5, ml: 1 }}>
					{timePart}
				</Typography>
			</Box>
		);
	};

	const checkDetailsFilter = (detailsStart: DateTime | null, detailsEnd: DateTime | null, detailsFromDate: DateTime | null, detailsToDate: DateTime | null) => {
		if (!detailsFromDate && !detailsToDate) return true;
		if (!detailsStart && !detailsEnd) return false;

		if (!detailsStart || !detailsEnd) return false;

		if (detailsStart && detailsEnd) {
			if (detailsFromDate && detailsToDate) {
				if (detailsStart < detailsFromDate.startOf("day") || detailsEnd > detailsToDate.endOf("day")) {
					return false;
				}
			} else if (detailsFromDate) {
				if (detailsStart < detailsFromDate.startOf("day")) {
					return false;
				}
			} else if (detailsToDate) {
				if (detailsEnd > detailsToDate.endOf("day")) {
					return false;
				}
			}
		} else if (detailsStart) {
			if (detailsFromDate && detailsStart < detailsFromDate.startOf("day")) {
				return false;
			}
			if (detailsToDate && detailsStart > detailsToDate.endOf("day")) {
				return false;
			}
		} else if (detailsEnd) {
			if (detailsFromDate && detailsEnd < detailsFromDate.startOf("day")) {
				return false;
			}
			if (detailsToDate && detailsEnd > detailsToDate.endOf("day")) {
				return false;
			}
		}
		return true;
	};
	const getDetails = (details: Record<string, string | null>) => {
		if (!details) {
			return "";
		}

		if (!details.start_time && !details.end_time) {
			return "";
		}

		if (details.start_time && !details.end_time) {
			const startDate = DateTime.fromISO(details.start_time);
			if (!startDate.isValid) {
				return "";
			}
			return formatTimestamp(startDate);
		}

		if (!details.start_time && details.end_time) {
			const endDate = DateTime.fromISO(details.end_time);
			if (!endDate.isValid) {
				return "";
			}
			return formatTimestamp(endDate);
		}

		// Both dates exist
		const startDate = DateTime.fromISO(details.start_time!);
		const endDate = DateTime.fromISO(details.end_time!);

		if (!startDate.isValid || !endDate.isValid) {
			return "";
		}

		if (startDate.equals(endDate)) {
			return (
				<Box component="span">
					<Typography component="span" sx={{ opacity: 1 }}>
						{startDate.toFormat("ccc, MMM d, yyyy h:mm a")}
					</Typography>
				</Box>
			);
		}

		const sameDay = startDate.hasSame(endDate, "day");
		return (
			<Box component="span" sx={{ display: "flex", alignItems: "center" }}>
				<Typography component="span" sx={{ opacity: 1, ml: 1 }}>
					{startDate.toFormat("ccc, MMM d, yyyy h:mm a")}
				</Typography>
				<Typography component="span" sx={{ mx: 0.5 }}>
					-
				</Typography>
				<Typography component="span" sx={{ opacity: 1 }}>
					{sameDay ? endDate.toFormat("h:mm a") : endDate.toFormat("ccc, MMM d, yyyy h:mm a")}
				</Typography>
			</Box>
		);
	};

	const extractDateFromDetails = (changes: Record<string, string | null>) => {
		if (!changes) return null;
		if (!changes.start_time || !changes.end_time) return null;

		const startDate = changes.start_time ? DateTime.fromISO(changes.start_time) : null;
		const endDate = changes.end_time ? DateTime.fromISO(changes.end_time) : null;

		// Only return valid dates
		const validStartDate = startDate?.isValid ? startDate : null;
		const validEndDate = endDate?.isValid ? endDate : null;

		if (validStartDate && !validEndDate) return [validStartDate, null];
		if (!validStartDate && validEndDate) return [null, validEndDate];
		if (validStartDate && validEndDate) return [validStartDate, validEndDate];
		return null;
	};

	// Calculate filtered entries count for analytics
	const filteredEntries = sortedActivityLogs.filter((log) => {
		// Page source filter
		const pageSourceMatch = !pageFilter || log.pageSource === pageFilter;

		// Table name filter
		const tableNameMatch = !tableNameFilter || log.tableName === tableNameFilter;

		// Roles are an array, so this matches if ANY of them is the selected one.
		const roleMatch = !roleFilter || log.changerAdminRoles.includes(roleFilter);

		const reasonMatch =
			!reasonFilter ||
			(typeof log.changes?.reason === "string" && log.changes.reason.toLowerCase() === reasonFilter.toLowerCase()) ||
			(typeof log.changes?.message === "string" && log.changes.message.toLowerCase() === reasonFilter.toLowerCase());

		// Details filter
		const result = extractDateFromDetails(log.changes);
		const detailsStart = result?.[0] ?? null;
		const detailsEnd = result?.[1] ?? null;
		if (!checkDetailsFilter(detailsStart, detailsEnd, detailsFromDate, detailsToDate)) {
			return false;
		}

		return pageSourceMatch && tableNameMatch && reasonMatch && roleMatch;
	});

	return (
		<Box data-testid="activity-log-root" sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2, p: 3 }}>
			{/* Activity Log Title and Filters Section */}
			<Box
				data-testid="activity-log-filter-section"
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					gap: 2,
					backgroundColor: NEUTRAL[50],
					padding: "16px",
					borderRadius: "4px",
				}}
			>
				<Box sx={{ display: "flex", gap: 3, alignItems: "center", pb: 3 }}>
					<Typography
						sx={{
							color: INK.SECONDARY,
							fontSize: "1rem",
							fontWeight: "bold",
						}}
					>
						Activity Log
					</Typography>
					{titleTrailingNode}
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "end",
						gap: 2,
					}}
				>
					{/* Date Range Inputs */}
					<DateRangeSelector
						label={"Date range"}
						startDate={start}
						endDate={end}
						onStartDateChange={handleFromDateChange}
						onEndDateChange={handleToDateChange}
						removeBackground
						containerSX={{ gap: 1 }}
					/>
					<DateRangeSelector
						label={"Details date range"}
						startDate={detailsFromDate ?? undefined}
						endDate={detailsToDate ?? undefined}
						onStartDateChange={handleDetailsFromDateChange}
						onEndDateChange={handleDetailsToDateChange}
						removeBackground
						containerSX={{ gap: 1 }}
						onClear={clearDetailsDate}
					/>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(2,1fr)",
							gridTemplateRows: "auto",
							gap: 1,
						}}
					>
						{/* Filter Dropdowns */}
						<SystemAutoComplete
							value={performedForFilter ? { label: getNurseFormattedName(performedForFilter), value: performedForFilter } : null}
							onChange={handlePerformedForFilterChange}
							// DEFAULT: no options until the roster names them.
							options={(roster ? sortedActivityLogs : []).flatMap(({ changeeNurseId }, index, origin) => {
								if (!changeeNurseId || origin.findIndex((i) => i.changeeNurseId === changeeNurseId) !== index) {
									return [];
								}
								return [{ label: getNurseFormattedName(changeeNurseId), value: changeeNurseId }];
							})}
							textFieldProps={{ ...commonTextFieldProps, label: "Performed For" }}
						/>

						<SystemAutoComplete
							value={performedByFilter ? { label: performedByFilter, value: performedByFilter } : null}
							onChange={handlePerformedByFilterChange}
							options={sortedActivityLogs
								.filter((entry) => Boolean(entry.changerUsername))
								.reduce<UserInfo["username"][]>((unique, { changerUsername }) => {
									if (changerUsername && !unique.includes(changerUsername)) {
										return unique.concat(changerUsername);
									}
									return unique;
								}, [])
								.map((value) => ({ label: value, value }))}
							textFieldProps={{
								...commonTextFieldProps,
								label: "Performed By",
							}}
							sx={{ minWidth: minWidth }}
						/>

						{/* reason */}
						<SystemAutoComplete
							value={reasonFilter ? { label: reasonFilter, value: reasonFilter } : null}
							onChange={handleReasonFilterChange}
							options={reasonOptions}
							textFieldProps={{
								...commonTextFieldProps,
								label: "Reason",
							}}
						/>

						<SystemDropdown
							selectedItem={pageFilter || undefined}
							onChange={handlePageFilterChange}
							label="Page / Source"
							clearable
							items={sortedActivityLogs
								.filter((entry) => Boolean(entry.pageSource))
								.reduce<string[]>((unique, entry) => (unique.includes(entry.pageSource) ? unique : unique.concat(entry.pageSource)), [])}
							selectSx={{ minWidth: 200 }}
						/>

						{/* From ADMIN_ROLES, not the loaded rows: a role with no activity is still selectable.
						    The value stays the raw enum (it is what filters the rows); only the label is translated. */}
						<SystemDropdown
							selectedItem={roleFilter}
							onChange={handleRoleFilterChange}
							label={t("activityLog.raw.filter.role")}
							clearable
							items={ADMIN_ROLES.map((role) => ({ value: role, label: t(`admin.roles.${role}`) }))}
							selectSx={{ minWidth: 200 }}
							data-testid="activity-log-role-filter"
						/>

						<SystemDropdown
							selectedItem={tableNameFilter || undefined}
							onChange={handleTableNameFilterChange}
							label="Table Name"
							clearable
							items={sortedActivityLogs
								.filter((entry) => Boolean(entry.tableName))
								.reduce<string[]>((unique, entry) => (unique.includes(entry.tableName) ? unique : unique.concat(entry.tableName)), [])}
							selectSx={{ minWidth: 200 }}
						/>
					</Box>
				</Box>
			</Box>
			{/* Table Section */}
			<Box
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				<TableContainer
					data-testid="activity-log-table"
					sx={{
						flex: 1,
						overflow: "auto",
						"&::-webkit-scrollbar": {
							width: "6px",
							height: "6px",
						},
						"&::-webkit-scrollbar-track": {
							background: NEUTRAL[100],
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb": {
							background: NEUTRAL[500],
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb:hover": {
							background: NEUTRAL[600],
						},
					}}
				>
					<Table
						size="small"
						stickyHeader
						sx={{
							tableLayout: "fixed",
							width: "100%",
							"& .MuiTableCell-root": {
								boxSizing: "border-box",
								padding: "10px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								textAlign: "center",
							},
							"& thead th": {
								height: "36px",
								background: (theme) => `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.08)}), #FFFFFF`,
								borderBottom: "1px solid #E1E6EF",
								color: INK.SECONDARY,
								fontSize: "0.875rem",
								fontWeight: "bold",
								padding: "13px 10px",
								textAlign: "center",
								position: "sticky",
								top: 0,
								zIndex: 1,
								"&:first-of-type": {
									width: "60px",
								},
								"&:not(:first-of-type)": {
									// 7 flexible columns: 9 total, minus "#" and Details.
									width: "calc((100% - 60px - 400px) / 7)",
								},
								"&:nth-of-type(7)": {
									width: "400px",
								},
							},
							"& tbody td": {
								height: "54px",
								background: "#FFFFFF",
								borderBottom: "1px solid #E1E6EF",
								color: INK.SECONDARY,
								fontSize: "0.875rem",
								textAlign: "center",
								"&:nth-of-type(7)": {
									overflowX: "auto",
									textOverflow: "clip",
								},
							},
						}}
					>
						<TableHead>
							<TableRow>
								<TableCell>#</TableCell>
								<TableHeader field="timestamp" label="Timestamp" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableHeader field="changerUsername" label="Performed By" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableCell sx={{ color: INK.SECONDARY, fontWeight: "bold" }}>{t("activityLog.raw.column.role")}</TableCell>
								<TableHeader field="pageSource" label="Page / Source" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableHeader field="action" label="Action" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableCell>Details</TableCell>
								<TableHeader field="changeeNurseId" label="Performed For" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableCell>Reason / Comment</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading
								? Array.from({ length: 10 }).map((_, index) => (
										<TableRow key={index.toString()}>
											<TableCell>
												<Skeleton variant="text" width={20} />
											</TableCell>
											<TableCell>
												<Skeleton variant="text" width="80%" />
											</TableCell>
											<TableCell>
												<Skeleton variant="text" width="70%" />
											</TableCell>
											<TableCell>
												<Skeleton variant="text" width="60%" />
											</TableCell>
											<TableCell>
												<Skeleton variant="text" width="50%" />
											</TableCell>
											<TableCell>
												<Skeleton variant="text" width="75%" />
											</TableCell>
											<TableCell>
												<Skeleton variant="text" width="90%" />
											</TableCell>
											<TableCell>
												<Skeleton variant="text" width="70%" />
											</TableCell>
											<TableCell>
												<Skeleton variant="text" width="80%" />
											</TableCell>
										</TableRow>
									))
								: isRosterError
									? [
											<TableRow key="roster-error">
												<TableCell colSpan={9} sx={{ textAlign: "center" }} data-testid="activity-log-roster-error">
													{t("profile.roster.unavailable")}
												</TableCell>
											</TableRow>,
										]
									: filteredEntries.slice(0, 500).map((entry, index) => (
											<TableRow
												key={index.toString()}
												onClick={() =>
													trackEvent("activity_log_row_clicked", {
														department_id: chosenDepartment?.department_id,
														entry_index: index,
														performed_by: entry.changerUsername,
														page_source: entry.pageSource,
														action: entry.action,
														table_name: entry.tableName,
													})
												}
												hover
												sx={{ cursor: "pointer" }}
											>
												<TableCell>{index + 1}</TableCell>
												<TableCell>{formatTimestamp(DateTime.fromISO(entry.timestamp))}</TableCell>
												<TableCell>{entry.changerUsername}</TableCell>
												{/* No role, or a masked changer: empty cell, row still shows. */}
												<TableCell>{entry.changerAdminRoles.map((role) => t(`admin.roles.${role}`)).join(", ")}</TableCell>
												<TableCell>
													<ChangeSourceBadge source={entry.pageSource} emptyLabel="" />
												</TableCell>
												<TableCell>
													{entry.action} {entry.tableName}
												</TableCell>
												<TableCell>{getDetails(entry.changes)}</TableCell>
												<TableCell>
													{!!entry.changeeNurseId && (
														<Tooltip title={getNurseFormattedName(entry.changeeNurseId)} placement="top">
															<Box
																component="span"
																sx={{
																	display: "block",
																	width: "100%",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																	whiteSpace: "nowrap",
																}}
															>
																{getNurseFormattedName(entry.changeeNurseId)}
															</Box>
														</Tooltip>
													)}
												</TableCell>
												<TableCell>{entry.changes?.reason || entry.changes?.message || ""}</TableCell>
											</TableRow>
										))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Box>
	);
};
